<template>
    <div v-if="display" class="row">
        <div class="col">
            <sub-section title="Effect of higher charges" :showHelp="false">
                <p>
                    A difference of 0.1% a year in charges seems very little. But for every 0.1% a year you pay in extra
                    charges, this amounts to a total of <strong>{{ formatter.formatCurrency(model.extraCharge) }}</strong>
                    more in charges over the expected lifetime of your plan. This will be coming out of your pots if you
                    don't get any additional growth from paying the extra charges.
                    {{ model.additionalMessage }}
                    <strong v-if="model.displayConsolidationMessage">
                        Putting all your pots together with a low cost provider may save you 5 times this amount (compared
                        to paying total average charges of around 1%).
                    </strong>
                </p>
            </sub-section>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { FinishInfoResponse } from '@/guiide/ts/response/finishInfoResponse';
import Constants from "@/guiide/ts/constants";
import { formatter } from "@/shared/ts/formatter";
import { useStore } from "vuex";
import SubSection from "../common/SubSection.vue";

const props = defineProps({
    finishInfo: {
        type: Object as () => FinishInfoResponse,
        default: () => new FinishInfoResponse(false, 0, []),
    },
});

type Model = { extraCharge: number, additionalMessage: string, displayConsolidationMessage: boolean };

const store = useStore();
const display = computed<boolean>(() => store.state.configuration.finishDisplaysCharges);
const industryAvgCharge = computed<number>(() => props.finishInfo?.getIndustryAverageChargeResponse().charge ?? 0);

const model = computed<Model>(() => {
    const costAtOnePointFive = industryAvgCharge.value;
    const costAtOnePointSix = (1.6 / 1.5) * costAtOnePointFive;
    const diff = costAtOnePointSix - costAtOnePointFive;
    const extraCharge = Math.ceil(diff / Constants.IncomeStepSize) * Constants.IncomeStepSize;

    const additionalMessage = store.state.configuration.finishDisplaysChargesExtraMessage;
    const displayConsolidationMessage = store.state.configuration.finishDisplaysChargesShowsPotCnsldnMsg;
    return { extraCharge, additionalMessage, displayConsolidationMessage };
});

</script>
